import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarCheck, ClipboardList, TrendingUp, PoundSterling, Loader2, Plus, Trash2 } from "lucide-react";
import { getAdminCalendar, addCalendarEvent, deleteCalendarEvent } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const TYPE = {
  viewing: { color: "#8b5cf6", icon: CalendarCheck, label: "Viewing" },
  task: { color: "#b8935f", icon: ClipboardList, label: "Task" },
  chase: { color: "#0ea5e9", icon: TrendingUp, label: "Chase" },
  payment: { color: "#16a34a", icon: PoundSterling, label: "Payment" },
};
const dkey = (d) => new Date(d).toISOString().slice(0, 10);
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const AdminCalendar = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(true);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");
  const [selected, setSelected] = useState(dkey(new Date()));
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", date: dkey(new Date()), time: "09:00", type: "task", notes: "" });

  const load = () => getAdminCalendar().then((d) => setEvents(d.events || [])).catch(() => {}).finally(() => setBusy(false));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ title: "", date: selected, time: "09:00", type: "task", notes: "" }); setAddOpen(true); };

  const removeEvent = async (ev) => {
    if (ev.readonly || ev.system) { toast({ title: "This event is auto-generated and can't be deleted here." }); return; }
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try { await deleteCalendarEvent(ev.id); setEvents((es) => es.filter((x) => x.id !== ev.id)); toast({ title: "Event deleted" }); }
    catch { toast({ title: "Couldn't delete event", description: "Auto-generated events (viewings, chases) can't be removed here." }); }
  };
  const saveEvent = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast({ title: "Add a title" }); return; }
    setSaving(true);
    try {
      await addCalendarEvent({ title: form.title, date: `${form.date}T${form.time || "09:00"}:00`, type: form.type, notes: form.notes });
      toast({ title: "Event added" });
      setAddOpen(false); setSelected(form.date); load();
    } catch { toast({ title: "Couldn't add event" }); }
    finally { setSaving(false); }
  };

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { const k = dkey(e.date); (m[k] = m[k] || []).push(e); });
    return m;
  }, [events]);

  const grid = useMemo(() => {
    const y = cursor.getFullYear(), mo = cursor.getMonth();
    const first = new Date(y, mo, 1);
    const startDow = (first.getDay() + 6) % 7; // Mon=0
    const start = new Date(y, mo, 1 - startDow);
    if (view === "week") {
      const t = new Date(selected);
      const wd = (t.getDay() + 6) % 7;
      const ws = new Date(t); ws.setDate(t.getDate() - wd);
      return Array.from({ length: 7 }, (_, i) => { const d = new Date(ws); d.setDate(ws.getDate() + i); return d; });
    }
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor, view, selected]);

  const move = (dir) => {
    const c = new Date(cursor);
    if (view === "week") c.setDate(c.getDate() + dir * 7); else c.setMonth(c.getMonth() + dir);
    setCursor(c);
  };

  const selectedEvents = (byDay[selected] || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));

  if (busy) return <div className="flex items-center justify-center py-24 text-[#8a8a8a]"><Loader2 className="animate-spin mr-2" size={18} /> Loading calendar…</div>;

  return (
    <div data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => move(-1)} data-testid="cal-prev" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center hover:bg-[#faf7f2]"><ChevronLeft size={16} /></button>
          <h3 className="font-display text-[22px] font-500 text-[#2b2b2b] min-w-[190px] text-center">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
          <button onClick={() => move(1)} data-testid="cal-next" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center hover:bg-[#faf7f2]"><ChevronRight size={16} /></button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-[#f0ece5] rounded-lg p-1">
            {["month","week"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`px-4 py-1.5 rounded-md text-[13px] capitalize transition-colors ${view === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a]"}`}>{v}</button>
            ))}
          </div>
          <button onClick={openAdd} data-testid="cal-add-event-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors"><Plus size={15} /> Add event</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="bg-white rounded-2xl border border-[#f0ece5] p-4">
          <div className="grid grid-cols-7 mb-2">{DOW.map((d) => <div key={d} className="text-center text-[11px] uppercase tracking-wide text-[#a0a0a0]">{d}</div>)}</div>
          <div className={`grid grid-cols-7 gap-1.5`}>
            {grid.map((d, i) => {
              const k = dkey(d);
              const evs = byDay[k] || [];
              const inMonth = d.getMonth() === cursor.getMonth() || view === "week";
              const isToday = k === dkey(new Date());
              const isSel = k === selected;
              return (
                <button key={i} onClick={() => setSelected(k)} data-testid="cal-day"
                  className={`min-h-[74px] rounded-lg border p-1.5 text-left transition-colors ${isSel ? "border-[#b8935f] bg-[#faf7f2]" : "border-[#f0ece5] hover:border-[#e2d3bf]"} ${inMonth ? "" : "opacity-40"}`}>
                  <span className={`text-[12px] font-500 ${isToday ? "bg-[#1c1c1e] text-white rounded-full w-5 h-5 inline-flex items-center justify-center" : "text-[#4a4a4a]"}`}>{d.getDate()}</span>
                  <div className="mt-1 space-y-0.5">
                    {evs.slice(0, 3).map((e, j) => (
                      <div key={j} className="flex items-center gap-1 truncate">
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: (TYPE[e.type] || TYPE.task).color }} />
                        <span className="text-[10px] text-[#6a6a6a] truncate">{e.title}</span>
                      </div>
                    ))}
                    {evs.length > 3 && <span className="text-[10px] text-[#b8935f]">+{evs.length - 3} more</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#f0ece5] p-5">
          <p className="text-[12px] uppercase tracking-wide text-[#b8935f] mb-1">{new Date(selected).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
          <p className="text-[13px] text-[#8a8a8a] mb-4">{selectedEvents.length} event{selectedEvents.length !== 1 ? "s" : ""}</p>
          <div className="space-y-3" data-testid="cal-day-events">
            {selectedEvents.length === 0 && <p className="text-[13px] text-[#c0c0c0]">Nothing scheduled.</p>}
            {selectedEvents.map((e) => {
              const t = TYPE[e.type] || TYPE.task; const Icon = t.icon;
              return (
                <div key={e.id} className="flex items-start gap-3 pb-3 border-b border-[#f5f1ea] last:border-0 group">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${t.color}18` }}><Icon size={15} style={{ color: t.color }} /></span>
                  <div className="min-w-0 flex-1"><p className="text-[13px] font-500 text-[#2b2b2b] truncate">{e.title}</p><p className="text-[11px] text-[#a0a0a0]">{t.label}{e.meta?.priority ? ` · ${e.meta.priority}` : ""}{e.meta?.amount ? ` · £${Number(e.meta.amount).toLocaleString()}` : ""}</p></div>
                  <button onClick={() => removeEvent(e)} data-testid={`cal-delete-${e.id}`} title="Delete event" className="shrink-0 text-[#c9c2b6] hover:text-red-500 transition-colors p-1"><Trash2 size={14} /></button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogTitle className="font-display text-[22px] font-300 text-[#2b2b2b] mb-3">Add calendar event</DialogTitle>
          <form onSubmit={saveEvent} className="space-y-3" data-testid="cal-add-form">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Event title" data-testid="cal-event-title" className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
            <div className="grid grid-cols-2 gap-3">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cal-event-date" className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] text-[#4a4a4a] outline-none focus:border-[#b8935f]" />
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="cal-event-time" className="w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] text-[#4a4a4a] outline-none focus:border-[#b8935f]" />
            </div>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="cal-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>{[["task", "Task"], ["viewing", "Viewing"], ["chase", "Chase / follow-up"], ["payment", "Payment"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes (optional)" className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f] resize-none" />
            <button type="submit" disabled={saving} data-testid="cal-event-save" className="w-full h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add to calendar</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendar;
